import { isNumber } from 'lodash/fp';
import { Theme } from '@material-ui/core';
import { translate } from '../../../../utils';
import { HeadCell } from '../../../../ui-kit/Table/types';
import { CurrentInventoryCountQuery } from '../../../../generated/graphql';
import { InventoryCountsFormikValues } from './types';

export interface InventoryCountsRow {
    id: string;
    startDate: string;
    expectedValuation: string;
    realValuation: string;
    endDate: string;
    shortage: string;
    surplus: string;
    delta: string;
}

export const inventoryCountsHeadCells: Array<HeadCell<InventoryCountsRow>> = [
    { id: 'startDate', align: 'left', label: translate('Date') },
    { id: 'expectedValuation', align: 'center', label: translate('Expected worth') },
    { id: 'realValuation', align: 'center', label: translate('Real worth') },
    { id: 'shortage', align: 'right', label: translate('Shortage') },
    { id: 'surplus', align: 'right', label: translate('Surplus') },
    { id: 'delta', align: 'right', label: translate('Balance') },
    { id: 'endDate', align: 'center', label: translate('Finish date') },
];

export interface InventoryCountDetailsRow {
    id: string;
    name: string;
    categoryName: string;
    expectedValuation: string;
    realValuation: string | null;
    realValuationNumber: number | null;
    expectedValuationNumber: number | null;
    expectedCount: string;
    realCount: string | null;
    // I think it can useful to show who did the last update
    // calculatedBy: string;
}

export const getInvnetoryCountDetailRowColor = (row: InventoryCountDetailsRow, theme: Theme) => {
    const { expectedCount, realCount } = row;
    if (realCount) {
        const realCountNumber = parseFloat(realCount);
        const expectedCountNumber = parseFloat(expectedCount);

        if (realCountNumber > expectedCountNumber) {
            return `${theme.palette.success.light}`;
        }

        if (realCountNumber < expectedCountNumber) {
            return theme.palette.error.light;
        }

        return undefined;
    }

    return theme.palette.error.light;
};

export const inventoryCountDetailsHeadCells: Array<HeadCell<InventoryCountDetailsRow>> = [
    { id: 'name', align: 'left', label: translate('Name') },
    { id: 'categoryName', align: 'left', label: translate('Category name') },
    { id: 'expectedValuation', align: 'center', label: translate('Expected worth') },
    { id: 'realValuation', align: 'center', label: translate('Real worth') },
    { id: 'expectedCount', align: 'center', label: translate('Expected count') },
    { id: 'realCount', align: 'center', label: translate('Real count') },
];


export const getTotalCountedItems = (formikValues: InventoryCountsFormikValues): number => {
    let countedItems = 0;
    formikValues.inventoryCountItems.forEach(({ realCount }) => {
        if (isNumber(realCount)) {
            countedItems += 1;
        }
    });

    return countedItems;
};

export const calculateProgress = (countedItems: number, totalValues: number): number => {
    const progress = (countedItems / totalValues) * 100;
    const progressString = progress.toString().slice(0, 3);
    return parseFloat(progressString);
};


interface GetFilteredItemsArgs {
    items: CurrentInventoryCountQuery['currentInventoryCount']['inventoryCountItems'];
    stockCardName: string;
    stockCardCategoryId: string | null;
}


type InventoryCountItem = InventoryCountsFormikValues['inventoryCountItems'][0];
interface CountItemsWithIndex extends InventoryCountItem {
    index: number;
}

export const getFilteredItems = ({
    items,
    stockCardName,
    stockCardCategoryId,
}: GetFilteredItemsArgs) => {
    const filteredCountItems: CountItemsWithIndex[] = [];
    items.forEach((item, index) => {
        const mappedItem = {
            index,
            id: item.id,
            realCount: item.realCount,
            unitType: item.stockCard.storageUnit,
            alternativeUnit: item.stockCard.conversionUnit,
            name: item.stockCard.name,
            categoryName: item.stockCard.stockCardCategory?.name,
        };

        const isTargetName = stockCardName ? item.stockCard.name.toLowerCase().includes(stockCardName.toLowerCase()) : true;
        if (stockCardCategoryId === null && isTargetName) {
            filteredCountItems.push(mappedItem);
        } else {
            const isTargetCategory = stockCardCategoryId === item.stockCard.stockCardCategoryId;
            if (isTargetCategory && isTargetName) {
                filteredCountItems.push(mappedItem);
            }
        }
    });
    return filteredCountItems;
};